import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { ErrorCodes } from '@shared/models/error-codes/response.error-codes';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { NgRedux } from '@angular-redux/store';
import { IAppState, Actions, AuthResponse } from '@core/redux/stores';

import {
  ProxyService,
  AlertService,
  ConfirmModalService,
  ValidationService,
  ModalService,
  AuthenticationService,
  AuthTrackingService,
  CustomizeHttpheaderService} from '@core/services';
import { Subscription } from 'rxjs';
import { LoggingService } from '@core/services/logging.service';
import { RequestUser, LoginIntent } from '../models';
import { Constants } from '@app/core/constants/core.constants';

@Component({
  selector: 'app-temporary-password',
  templateUrl: './temporary-password.component.html',
  styleUrls: ['./temporary-password.component.less'],
  providers: [AlertService]
})
export class TemporaryPasswordComponent implements OnInit, AfterViewInit, OnDestroy {

  public tempPwdForm: UntypedFormGroup;
  private userEmail: string;
  private returnUrl: string;
  public notReady: boolean;
  public loading: boolean;

  @ViewChild('tempPassword', { static: true }) tempPassword: any;

  private subscriptions = new Subscription();
  public temPassword: string;

  constructor(
    public alertService: AlertService,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private proxyService: ProxyService,
    private ngRedux: NgRedux<IAppState>,
    private activatedRoute: ActivatedRoute,
    private confirmModalService: ConfirmModalService,
    private router: Router,
    private logger: LoggingService,
    private registrationService: AuthenticationService,
    public authTrackingService: AuthTrackingService,
    private customizeHeaderService: CustomizeHttpheaderService
  ) {
    this.returnUrl = '';
    this.createTempPasswordForm();
    this.userEmail = sessionStorage.getItem('userEmail');
    this.notReady = true;
    this.loading = false;
    this.temPassword = null;

    this.subscriptions.add(this.confirmModalService.btnEvent.subscribe(
      (data: string) => {
        if (data === 'resendPwd') {
          this.resendEmail();
        } else if (data === 'resentEmail') {
          this.showModal('app-temporary-password');
          this.loading = false;
        } else if ( data === 'resetPasswordConfirm') {
          if (this.returnUrl) {
            this.router.navigate(['/' + this.returnUrl]);
          }
          this.modalService.hideModal();
        } else if (data === 'resetPWBtn') {
          sessionStorage.setItem('email', this.userEmail);
          this.modalService.hideModal();
          this.showModal('app-forgot-password');
        } else {
          this.showModal(this.modalService.getSignInModalSelector());
        }
      }
    ));
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.returnUrl = params[Constants.RETURN_URL] as string;
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  showModal(name: string) {
    this.modalService.setModalSelector(name);
    this.modalService.showModal();
  }

  createTempPasswordForm() {
    this.tempPwdForm = this.formBuilder.group({
      temporaryPassword: new UntypedFormControl(this.temPassword, [Validators.required]),
      password: new UntypedFormControl(null, [Validators.required, ValidationService.passwordValidator]),
      confPw: new UntypedFormControl(null, [Validators.required])
    },
      { validator: ValidationService.passwordMatch }
    );

    this.tempPwdForm.valueChanges.subscribe(values => {
      this.checkReady();
    });
  }

  checkReady() {
    if (this.tempPwdForm.valid) {
      this.notReady = false;
    } else {
      this.notReady = true;
    }
  }

  processErr(errorResponse: any) {
    this.alertService.error(errorResponse.message);
    this.logger.logError(errorResponse);
    this.loading = false;
  }

  changePassword() {
    if (this.tempPwdForm.dirty && this.tempPwdForm.valid) {
      const payload = {
        'password': this.tempPwdForm.value.temporaryPassword,
        'newPassword': this.tempPwdForm.value.password,
        'refreshToken': this.authTrackingService.getRefreshToken()
      };
      this.temPassword = this.tempPwdForm.value.temporaryPassword;
      this.loading = true;
      const response = this.proxyService.httpPost(ApiRoutes.CHANGEPW, payload).subscribe(
        (success) => {
          this.loading = false;
          this.onChangePwdSuccess();
        },
        (err) => {
          this.loading = false;

          if (err.status === 200) {
              this.onChangePwdSuccess();
          } else if (
            err.error.code === ErrorCodes.INCORRECT_PWD
            || err.error.code === ErrorCodes.INVALID_PWD || err.error.code === ErrorCodes.INCORRECT_USERNAME_OR_PWD) {
              this.invalidTempPwd();
          } else if (err.error.code === ErrorCodes.TEMP_PWD_EXPIRED) {
            this.tempPwdExpired();
          } else {
            this.processErr(err);
          }
        });
    }
  }

  invalidTempPwd() {
    this.confirmModalService.show(
      'Temporary Password Invalid',
      '',
      'Uh-oh, the temporary password is not valid. We\'ll be glad to send you another one.',
      'Resend Password',
      '',
      'Cancel',
      true,
      'resendPwd'
    );
  }

  tempPwdExpired() {
    this.confirmModalService.show(
      'Temporary Password Expired',
      '',
      'Uh-oh, the temporary password is no longer valid. We\'ll be glad to send you another one.',
      'Resend Password',
      '',
      'Cancel',
      true,
      'resendPwd'
    );
  }

  checkTempPwd() {
    const payload: RequestUser = {
      email: this.userEmail,
      password: this.tempPwdForm.value.temporaryPassword,
      deviceDetails: null,
      intent: LoginIntent.ForgotPassword
    };

    this.loading = true;
    this.proxyService.httpPost(ApiRoutes.LOGIN, payload,
      this.customizeHeaderService.addLongAndLat()).subscribe(
      (response) => {
        const signInResponse = <AuthResponse>response.body.valueOf();
        signInResponse.user.isEmailVerified = this.registrationService.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
        this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
        this.registrationService.updateBrazeUser(signInResponse.user);
        this.changePassword();
    },
      (err) => {
        this.loading = false;

        if (err.error.code === ErrorCodes.INCORRECT_PWD ||
          err.error.code === ErrorCodes.INVALID_PWD || err.error.code === ErrorCodes.INCORRECT_USERNAME_OR_PWD) {
          this.invalidTempPwd();
        } else if (err.error.code === ErrorCodes.TEMP_PWD_EXPIRED) {
          this.tempPwdExpired();
        } else if (err.error.code === ErrorCodes.ACCOUNT_LCK) {
          this.accountLock();
        } else if (err.error.code === 4037) {
          this.loading = true;
          this.registrationService.showOKModal(err.error.code);
          this.loading = false;
        } else {
          this.processErr(err);
        }
      }
    );
  }

  accountLock() {
    this.confirmModalService.show('Account is now locked',
    '',
    'Due to 3 unsuccessful sign in attempts, this account is locked for 30 minutes. '
    + 'You may reset your password now or try again later.',
    'Reset Password',
    '',
    'Try Again Later',
    true,
    'resetPWBtn');
  }

  resendEmail() {
    const payload = {
      'email': this.userEmail
    };

    this.loading = true;
    const response = this.proxyService.httpPost(ApiRoutes.RESETPW, payload).subscribe(
      (success) => {
        if (success.status === 200) {
          this.temPassword = null;
          this.tempPassword.nativeElement.focus();
          this.confirmModalService.show(
            'Mail sent!',
            '',
            'Email sent successfully.',
            'OK, Got It',
            '',
            '',
            true,
            'resentEmail'
          );          
        } else {
          this.loading = false;
        }
      },
      (err) => {
        if (err.status === 200) {
          this.confirmModalService.show(
            'Mail sent!',
            '',
            'Email sent successfully.',
            'OK, Got It',
            '',
            '',
            true,
            'resentEmail'
          );
          this.loading = false;
        } else {
          this.processErr(err);
        }
      });
  }


  onChangePwdSuccess() {
    this.loading = false;
    this.confirmModalService.show(
      'Success!',
      '',
      'We\'ve reset your password, and you\'re back in business.',
      'OK, Got It',
      '',
      '',
      true,
      'resetPasswordConfirm'
    );
  }
}
